const toStringSafe = (value) => {
  if (value === undefined || value === null) return "";
  return String(value).trim();
};

const tryParseJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  const raw = toStringSafe(value);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const CANONICAL_LINKS = {
  about: "/about",
  "about us": "/about",
  careers: "/careers",
  blog: "/blog",
  privacy: "/privacy",
  "privacy policy": "/privacy",
  terms: "/terms",
  "terms & conditions": "/terms",
  "terms and conditions": "/terms",
  help: "/help",
  "help center": "/help",
  contact: "/contact",
  "contact us": "/contact",
  "partner with us": "/partner-with-us",
};

const repairFooterLinkUrl = (label, url) => {
  const nextUrl = toStringSafe(url || "");
  if (nextUrl) return nextUrl;
  const normalizedLabel = toStringSafe(label).toLowerCase();
  return CANONICAL_LINKS[normalizedLabel] || "";
};

const normalizeFooterLinks = (items) =>
  tryParseJsonArray(items)
    .map((item, index) => ({
      label: toStringSafe(item?.label || item?.title),
      url: repairFooterLinkUrl(
        item?.label || item?.title,
        item?.url || item?.href || item?.link || item?.path
      ),
      sort_order: Number.isFinite(Number(item?.sort_order))
        ? Number(item.sort_order)
        : index + 1,
      is_active: item?.is_active === false ? false : true,
    }))
    .filter((item) => item.is_active && (item.label || item.url))
    .sort((left, right) => left.sort_order - right.sort_order);

const ensureBlogLink = (items = []) => {
  const hasBlogLink = items.some(
    (item) => toStringSafe(item?.url).toLowerCase() === "/blog" || toStringSafe(item?.label).toLowerCase() === "blog"
  );
  if (hasBlogLink) return items;
  return [
    ...items,
    {
      label: "Blog",
      url: "/blog",
      sort_order: items.length + 1,
      is_active: true,
    },
  ];
};

const ensureLink = (items = [], label, url) => {
  const normalizedLabel = toStringSafe(label).toLowerCase();
  const normalizedUrl = toStringSafe(url).toLowerCase();
  const exists = items.some((item) => {
    const itemLabel = toStringSafe(item?.label).toLowerCase();
    const itemUrl = toStringSafe(item?.url).toLowerCase();
    return itemLabel === normalizedLabel || itemUrl === normalizedUrl;
  });
  if (exists) return items;
  return [
    ...items,
    {
      label,
      url,
      sort_order: items.length + 1,
      is_active: true,
    },
  ];
};

const normalizeSocialLinks = (items) =>
  tryParseJsonArray(items)
    .map((item, index) => ({
      platform: toStringSafe(item?.platform || item?.key).toLowerCase(),
      label: toStringSafe(item?.label),
      url: toStringSafe(item?.url || item?.href || item?.link),
      sort_order: Number.isFinite(Number(item?.sort_order))
        ? Number(item.sort_order)
        : index + 1,
      is_active: item?.is_active === false ? false : true,
    }))
    .filter((item) => item.is_active && item.url)
    .sort((left, right) => left.sort_order - right.sort_order);

const normalizeFaqItems = (items) =>
  tryParseJsonArray(items)
    .map((item, index) => ({
      id: item?.id || `faq-${index + 1}`,
      question: toStringSafe(item?.question || item?.q || item?.title),
      answer: toStringSafe(item?.answer || item?.a || item?.body),
      sort_order: Number.isFinite(Number(item?.sort_order))
        ? Number(item.sort_order)
        : index + 1,
      is_active: item?.is_active === false ? false : true,
    }))
    .filter((item) => item.is_active && item.question && item.answer)
    .sort((left, right) => left.sort_order - right.sort_order);

const normalizePartnerItems = (items) =>
  tryParseJsonArray(items)
    .map((item, index) => ({
      name: toStringSafe(item?.name || item?.label || item?.title),
      logo_url: toStringSafe(item?.logo_url || item?.logoUrl || item?.logo),
      url: toStringSafe(item?.url || item?.href || item?.link),
      alt_text: toStringSafe(item?.alt_text || item?.altText || item?.name || item?.label),
      sort_order: Number.isFinite(Number(item?.sort_order))
        ? Number(item.sort_order)
        : index + 1,
      is_active: item?.is_active === false ? false : true,
    }))
    .filter((item) => item.is_active && (item.name || item.logo_url || item.url))
    .sort((left, right) => left.sort_order - right.sort_order);

const DEFAULT_NEWSLETTER_COPY = {
  eyebrow: "GET THE LATEST CURATED JOURNEYS",
  title: "Get the latest curated journeys",
  description: "Insider access to new packages, seasonal offers, and private retreats.",
  placeholder: "Enter your email",
  buttonLabel: "Subscribe",
};

const DEFAULT_COMPACT_NEWSLETTER_COPY = {
  title: "Get the latest curated journeys",
  description: "Insider access to new packages, seasonal offers, and private retreats.",
  placeholder: "Enter your email",
  buttonLabel: "Subscribe",
};

const DEFAULT_CTA_COPY = {
  eyebrow: "PARTNER WITH US",
  title: "List your property with Homevilas",
  description: "Join Homevilas to grow bookings, reach qualified guests, and build a stronger luxury stay brand.",
  buttonLabel: "Partner with us",
  buttonUrl: "/partner-with-us",
};

const DEFAULT_FAQ_COPY = {
  eyebrow: "FAQ",
  title: "Frequently asked questions",
  description: "Quick answers to help you book confidently.",
  searchPlaceholder: "Search questions",
};

const looksLikeNewsletterCopy = (value) => {
  const normalized = toStringSafe(value).toLowerCase();
  if (!normalized) return false;
  return (
    normalized === DEFAULT_NEWSLETTER_COPY.eyebrow.toLowerCase() ||
    normalized === DEFAULT_NEWSLETTER_COPY.title.toLowerCase()
  );
};

const shouldRepairCtaCopy = (raw, ctaEnabled) => {
  if (!ctaEnabled) return false;

  const eyebrow = toStringSafe(raw.cta_eyebrow);
  const title = toStringSafe(raw.cta_title);
  const description = toStringSafe(raw.cta_description);
  const buttonLabel = toStringSafe(raw.cta_button_label);
  const buttonUrl = toStringSafe(raw.cta_button_url);

  const isIncomplete = !description || !buttonLabel || !buttonUrl;
  const looksLikePlaceholder =
    !title ||
    /^curated journeys?$/i.test(title) ||
    /^ready to design your next journey\??$/i.test(title) ||
    looksLikeNewsletterCopy(eyebrow) ||
    looksLikeNewsletterCopy(title) ||
    eyebrow.toLowerCase() === "custom journeys";

  return isIncomplete && looksLikePlaceholder;
};

const shouldRepairLegacyCtaCard = (raw, ctaEnabled) => {
  if (!ctaEnabled) return false;
  const eyebrow = toStringSafe(raw.cta_eyebrow).toLowerCase();
  const title = toStringSafe(raw.cta_title).toLowerCase();
  const label = toStringSafe(raw.cta_button_label).toLowerCase();
  const url = toStringSafe(raw.cta_button_url).toLowerCase();

  const matchesLegacyCopy =
    eyebrow === "custom journeys" ||
    title.includes("design your next journey") ||
    label === "request a custom tour" ||
    url === "/custom-tour";

  return matchesLegacyCopy;
};

export const EMPTY_PUBLIC_FOOTER = {
  brand_name: "Homevilas",
  brand_tagline: "",
  about_text: "",
  contact_email: "",
  contact_phone: "",
  contact_address: "",
  company_links: [],
  support_links: [],
  places_links: [],
  newsletter_enabled: false,
  newsletter_eyebrow: "",
  newsletter_title: "",
  newsletter_description: "",
  newsletter_placeholder: "",
  newsletter_button_label: "",
  newsletter_compact_title: "",
  newsletter_compact_description: "",
  newsletter_compact_placeholder: "",
  newsletter_compact_button_label: "",
  cta_enabled: false,
  cta_eyebrow: "",
  cta_title: "",
  cta_description: "",
  cta_button_label: "",
  cta_button_url: "",
  faq_enabled: false,
  faq_eyebrow: "",
  faq_title: "",
  faq_description: "",
  faq_search_placeholder: "",
  faq_items: [],
  partners_enabled: false,
  partners_eyebrow: "",
  partners_title: "",
  partners_description: "",
  partner_items: [],
  social_links: [],
  copyright_text: "",
  is_active: true,
  updated_at: null,
};

export const normalizePublicFooterPayload = (payload) => {
  const raw =
    payload && typeof payload === "object" && payload.data && typeof payload.data === "object"
      ? payload.data
      : payload && typeof payload === "object"
        ? payload
        : {};

  const newsletterEnabled =
    raw.newsletter_enabled === false
      ? false
      : Boolean(
          raw.newsletter_enabled ||
            raw.newsletter_title ||
            raw.newsletter_description ||
            raw.newsletter_compact_title ||
            raw.newsletter_compact_description
        );
  const ctaEnabled =
    raw.cta_enabled === false
      ? false
      : Boolean(
          raw.cta_enabled ||
            raw.cta_title ||
            raw.cta_description ||
            raw.cta_button_label
        );
  const faqEnabled =
    raw.faq_enabled === false
      ? false
      : Boolean(
          raw.faq_enabled ||
            raw.faq_title ||
            raw.faq_description ||
            (Array.isArray(raw.faq_items) && raw.faq_items.length)
        );
  const repairCtaCopy = shouldRepairCtaCopy(raw, ctaEnabled);
  const repairLegacyCtaCard = shouldRepairLegacyCtaCard(raw, ctaEnabled);
  const useDefaultCtaCard = repairCtaCopy || repairLegacyCtaCard;

  return {
    ...EMPTY_PUBLIC_FOOTER,
    brand_name: toStringSafe(raw.brand_name) || EMPTY_PUBLIC_FOOTER.brand_name,
    brand_tagline: toStringSafe(raw.brand_tagline),
    about_text: toStringSafe(raw.about_text),
    contact_email: toStringSafe(raw.contact_email),
    contact_phone: toStringSafe(raw.contact_phone),
    contact_address: toStringSafe(raw.contact_address),
    company_links: ensureLink(
      ensureBlogLink(normalizeFooterLinks(raw.company_links)),
      "Partner with us",
      "/partner-with-us"
    ),
    support_links: ensureLink(normalizeFooterLinks(raw.support_links), "Privacy", "/privacy"),
    places_links: normalizeFooterLinks(raw.places_links),
    newsletter_enabled: newsletterEnabled,
    newsletter_eyebrow:
      toStringSafe(raw.newsletter_eyebrow) ||
      (newsletterEnabled ? DEFAULT_NEWSLETTER_COPY.eyebrow : ""),
    newsletter_title:
      toStringSafe(raw.newsletter_title) ||
      (newsletterEnabled ? DEFAULT_NEWSLETTER_COPY.title : ""),
    newsletter_description:
      toStringSafe(raw.newsletter_description) ||
      (newsletterEnabled ? DEFAULT_NEWSLETTER_COPY.description : ""),
    newsletter_placeholder:
      toStringSafe(raw.newsletter_placeholder) ||
      (newsletterEnabled ? DEFAULT_NEWSLETTER_COPY.placeholder : ""),
    newsletter_button_label:
      toStringSafe(raw.newsletter_button_label) ||
      (newsletterEnabled ? DEFAULT_NEWSLETTER_COPY.buttonLabel : ""),
    newsletter_compact_title:
      toStringSafe(raw.newsletter_compact_title) ||
      (newsletterEnabled ? DEFAULT_COMPACT_NEWSLETTER_COPY.title : ""),
    newsletter_compact_description:
      toStringSafe(raw.newsletter_compact_description) ||
      (newsletterEnabled ? DEFAULT_COMPACT_NEWSLETTER_COPY.description : ""),
    newsletter_compact_placeholder:
      toStringSafe(raw.newsletter_compact_placeholder) ||
      (newsletterEnabled ? DEFAULT_COMPACT_NEWSLETTER_COPY.placeholder : ""),
    newsletter_compact_button_label:
      toStringSafe(raw.newsletter_compact_button_label) ||
      (newsletterEnabled ? DEFAULT_COMPACT_NEWSLETTER_COPY.buttonLabel : ""),
    cta_enabled: ctaEnabled,
    cta_eyebrow: useDefaultCtaCard
      ? DEFAULT_CTA_COPY.eyebrow
      : toStringSafe(raw.cta_eyebrow) || (ctaEnabled ? DEFAULT_CTA_COPY.eyebrow : ""),
    cta_title: useDefaultCtaCard
      ? DEFAULT_CTA_COPY.title
      : toStringSafe(raw.cta_title) || (ctaEnabled ? DEFAULT_CTA_COPY.title : ""),
    cta_description:
      (useDefaultCtaCard ? "" : toStringSafe(raw.cta_description)) ||
      (ctaEnabled ? DEFAULT_CTA_COPY.description : ""),
    cta_button_label:
      (useDefaultCtaCard ? "" : toStringSafe(raw.cta_button_label)) ||
      (ctaEnabled ? DEFAULT_CTA_COPY.buttonLabel : ""),
    cta_button_url:
      (useDefaultCtaCard ? DEFAULT_CTA_COPY.buttonUrl : "") ||
      toStringSafe(raw.cta_button_url) ||
      (ctaEnabled ? DEFAULT_CTA_COPY.buttonUrl : ""),
    faq_enabled: faqEnabled,
    faq_eyebrow:
      toStringSafe(raw.faq_eyebrow) ||
      (faqEnabled ? DEFAULT_FAQ_COPY.eyebrow : ""),
    faq_title:
      toStringSafe(raw.faq_title) ||
      (faqEnabled ? DEFAULT_FAQ_COPY.title : ""),
    faq_description:
      toStringSafe(raw.faq_description) ||
      (faqEnabled ? DEFAULT_FAQ_COPY.description : ""),
    faq_search_placeholder:
      toStringSafe(raw.faq_search_placeholder) ||
      (faqEnabled ? DEFAULT_FAQ_COPY.searchPlaceholder : ""),
    faq_items: normalizeFaqItems(raw.faq_items),
    partners_enabled:
      raw.partners_enabled === false
        ? false
        : Boolean(
            raw.partners_enabled ||
              raw.partners_title ||
              raw.partners_description ||
              (Array.isArray(raw.partner_items) && raw.partner_items.length)
          ),
    partners_eyebrow: toStringSafe(raw.partners_eyebrow),
    partners_title: toStringSafe(raw.partners_title),
    partners_description: toStringSafe(raw.partners_description),
    partner_items: normalizePartnerItems(raw.partner_items),
    social_links: normalizeSocialLinks(raw.social_links),
    copyright_text: toStringSafe(raw.copyright_text),
    is_active: raw.is_active === false ? false : true,
    updated_at: raw.updated_at || null,
  };
};

export const extractFooterSupport = (payload) => {
  const footer = normalizePublicFooterPayload(payload);
  return {
    email: footer.contact_email,
    phone: footer.contact_phone,
    address: footer.contact_address,
  };
};

export const hasFooterContent = (footer) => {
  if (!footer || footer.is_active === false) return false;
  return Boolean(
    footer.brand_name ||
      footer.brand_tagline ||
      footer.about_text ||
      footer.contact_email ||
      footer.contact_phone ||
      footer.contact_address ||
      footer.company_links.length ||
      footer.support_links.length ||
      footer.places_links.length ||
      footer.newsletter_enabled ||
      footer.cta_enabled ||
      footer.faq_enabled ||
      footer.partner_items.length ||
      footer.social_links.length ||
      footer.copyright_text
  );
};
